export const generateNetWorthSummary = (data) => {
  const borderColor = '#9ca3af';
  const bigMargin = 20;
  const branchName = data.user?.branch?.branchName || '';
  const branchAddress = `${data?.user?.branch?.branchStreet} ${data?.user?.branch?.branchCity} ${data?.user?.branch?.branchProvince} ${data?.user?.branch?.branchCountry}`;
  // Get the main branch phone number
  const branchPhones = () => {
    if (!data?.user?.branch?.branchTelephone) return '';
    const mainBranch = data?.user?.branch?.branchTelephone.find(
      (t) => t.isMain == true
    );
    if (!mainBranch) return '';
    return mainBranch.number;
  };

  const tableLayout = {
    hLineColor: borderColor,
    vLineColor: borderColor,
    paddingBottom: () => 4,
    paddingTop: () => 4,
    paddingLeft: () => 2,
    paddingRight: () => 2
  };

  const accountHolders = () => {
    if (!data.loan?.loanDistributions) return '';
    return data.loan?.loanDistributions
      .map(
        (d) =>
          `${d.person?.firstName} ${d.person?.middleName} ${d.person?.lastName}`
      )
      .join(', ');
  };

  const holders = accountHolders();

  /**@type {import('pdfmake/interfaces').TDocumentDefinitions} */
  const dd = {
    footer: function (currentPage, pageCount) {
      return {
        text: `Page ${currentPage.toString()} of ${pageCount.toString()}`,
        alignment: 'center'
      };
    },
    info: {
      title: 'Net Worth Summary', // name of the document being printed
      modDate: new Date(), // date the document was last modified
      creationDate: new Date(), // date the document was created
      keywords: 'net-worth, loan, finance', // keywords associated with the document
      author: data.company?.name, // company name
      subject: 'Net Worth Summary', // name of the document being printed
      creator: data.user?.username, // name of the logged in user
      producer: data.company?.name // company name
    },
    content: [
      data.company?.companyImage && {
        image: 'companyImage',
        alignment: 'center',
        fit: [80, 80]
      },
      {
        marginTop: 20,
        text: data.company?.name,
        alignment: 'center',
        fontSize: 22,
        bold: true
      },
      {
        text: 'Statement of Net Worth',
        alignment: 'center',
        fontSize: 14,
        marginBottom: 10,
        bold: true
      },
      { text: branchName, alignment: 'center' },
      { alignment: 'center', text: branchAddress },
      { alignment: 'center', text: branchPhones() },
      {
        text: "(Borrower's Information)",
        margin: [0, 25, 0, 5]
      },
      {
        text: accountHolders(),
        bold: true,
        margin: [0, 0, 0, 5]
      },
      {
        text: `Account No. ${data?.loan?.mainAccountNumber}`,
        margin: [0, 0, 0, 5]
      },
      {
        text: `Branch: ${data.loan?.accreditedBranch?.branchName || ''}`,
        margin: [0, 0, 0, 5]
      },
      {
        text: `Loan Product: ${data.loan?.loanProduct || ''}`,
        margin: [0, 0, 0, 25]
      },
      {
        layout: tableLayout,
        table: {
          body: [
            [
              { text: 'Assets', bold: true },
              { text: 'Asset Type', bold: true },
              { text: 'Asset Description', bold: true },
              { text: 'Member Value', bold: true },
              { text: 'Internal Value', bold: true }
            ],
            [
              { text: '', bold: false },
              { text: '', bold: false },
              { text: '', bold: false },
              { text: '', bold: false },
              { text: '$ 0.00', bold: true }
            ],
            [
              { text: 'Liabilities', bold: true },
              { text: '', bold: true },
              { text: 'Liabilities Type', bold: true },
              { text: 'Liabilities Description', bold: true },
              { text: 'Amount', bold: true }
            ],
            [
              { text: '', bold: false },
              { text: '', bold: false },
              { text: '', bold: false },
              { text: '', bold: false },
              { text: '$ 0.00', bold: false }
            ],
            [
              { text: 'Net Worth', bold: true },
              { text: '', bold: false },
              { text: '', bold: false },
              { text: '', bold: false },
              { text: 'Amount', bold: true }
            ],
            [
              { text: '', bold: false },
              { text: '', bold: false },
              { text: '', bold: false },
              { text: '', bold: false },
              { text: '', bold: false }
            ],
            [
              { text: 'Asset to Liability Ratio', bold: true },
              { text: '', bold: false },
              { text: '', bold: false },
              { text: '', bold: false },
              { text: 'Ratio', bold: true }
            ],
            [
              { text: '', bold: false },
              { text: '', bold: false },
              { text: '', bold: false },
              { text: '', bold: false },
              { text: '0', bold: false }
            ]
          ]
        }
      },
      {
        pageBreak: 'before',
        text: 'Declaration of Member',
        style: 'tableHeading'
      },
      {
        text: `I hereby confirm that the information given herein is true in all respects, accurate and complete and that I have not withheld any information that might affect the Credit Union’s decision. I am agreeable to the Credit Union taking such steps, as it may deem necessary to verify any of the information given.`,
        margin: [0, 0, 0, 10]
      },
      { text: 'Signatures', marginTop: bigMargin, bold: true },
      // Map over the holders and display a signature line for each along with their name below
      holders &&
        holders.split(',').map((holder) => {
          return [
            {
              columns: [
                { text: '_______________________________', font: 'Roboto' },
                {
                  text: '_______________________________',
                  font: 'Roboto',
                  alignment: 'right'
                }
              ],
              marginTop: 15
            },
            {
              marginTop: 10,
              columns: [
                {
                  text: `Borrower: ${holder}`,
                  style: 'supSignature',
                  alignment: 'left'
                },
                {
                  text: 'Date Signed',
                  alignment: 'right',
                  style: 'supSignature'
                }
              ]
            }
          ];
        }),
      {
        marginTop: 15,
        columns: [
          { text: '_______________________________', font: 'Roboto' },
          {
            text: '_______________________________',
            font: 'Roboto',
            alignment: 'right'
          }
        ]
      },
      {
        marginTop: 10,
        columns: [
          {
            text: `Prepared by: ${data.user?.username}`,
            style: 'supSignature',
            alignment: 'left'
          },
          { text: 'Date Signed', alignment: 'right', style: 'supSignature' }
        ]
      }
    ],
    images: {
      companyImage: data.company?.companyImage
    },
    styles: {
      tableHeader: {
        bold: true
      },
      supSignature: {
        fontSize: 18,
        sup: true,
        alignment: 'center'
      },
      tableHeading: {
        alignment: 'left',
        fontSize: 16,
        bold: true
      }
    },
    defaultStyle: {
      font: 'Lora',
      color: '#111827',
      fontSize: 11,
      lineHeight: 1.3
    }
  };
  return dd;
};
